import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type HelpPanelContextValue = {
  activeHelpKey: string | null;
  openHelp: (key: string) => void;
  closeHelp: () => void;
};

const HelpPanelContext = createContext<HelpPanelContextValue | null>(null);

export function HelpPanelProvider({ children }: { children: ReactNode }) {
  const [activeHelpKey, setActiveHelpKey] = useState<string | null>(null);

  const openHelp = useCallback((key: string) => {
    setActiveHelpKey((prev) => (prev === key ? null : key));
  }, []);

  const closeHelp = useCallback(() => {
    setActiveHelpKey(null);
  }, []);

  const value = useMemo(
    () => ({ activeHelpKey, openHelp, closeHelp }),
    [activeHelpKey, openHelp, closeHelp]
  );

  return (
    <HelpPanelContext.Provider value={value}>
      {children}
    </HelpPanelContext.Provider>
  );
}

/** @see HelpPanelProvider */
// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with provider
export function useHelpPanel(): HelpPanelContextValue {
  const ctx = useContext(HelpPanelContext);
  if (!ctx) {
    throw new Error('useHelpPanel must be used within HelpPanelProvider');
  }
  return ctx;
}
